import {
  CompletionIntervalType,
  IAllTasksFilters,
} from 'views/pages/Worklist/AllWorklist/types.ts';
import { isEqual } from 'lodash';
import { useAllWorklistContext } from 'views/pages/Worklist/AllWorklist/contexts/AllWorklistContext.ts';
import { useEffect, useState } from 'react';
import {
  useAllWorklistMetadataQuery,
  useBuildingsShortQuery,
  usePracticeShortQuery,
  useTaskFilterOptionsQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from 'common/cache';
import { SelectChangeEvent } from '@mui/material';

export type ICardFilters = Pick<
  IAllTasksFilters,
  | 'programs'
  | 'due_date'
  | 'category'
  | 'location_building_ids'
  | 'priorities'
  | 'completion_interval'
  | 'statuses'
  | 'team_id'
  | 'practice_id'
  | 'assignee_id'
  | 'task_names'
  | 'order_types'
  | 'order_categories'
  | 'last_updated_after'
>;

type Option<T> = {
  value: T;
  label: string;
};

const initialFilters: IAllTasksFilters = {
  search: '',
  programs: [],
  due_date: '',
  category: '',
  location_building_ids: [],
  priorities: ['Overdue', 'Urgent', 'High', 'Medium', 'Low'],
  sort_by: 'due_date',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  statuses: ['Completed'],
  completion_interval: 'month',
  team_id: '',
  assignee_id: '',
  partner_id: '',
  practice_id: '',
  last_updated_after: '',
  task_names: [],
  order_categories: [],
  order_types: [],
  provider_ids: [],
  appliedFilters: 0,
};

type keyOfFilters = keyof IAllTasksFilters;
const ignoreFieldsList = [
  'provider_ids',
  'partner_id',
  'sort_by',
  'sort_order',
  'search',
  'page',
  'limit',
  'appliedFilters',
];

const getAppliedFiltersCount = (filters: IAllTasksFilters) => {
  let count = 0;

  const keysInitial: keyOfFilters[] = Object.keys(
    initialFilters,
  ) as keyOfFilters[];

  keysInitial.forEach(key => {
    if (
      !isEqual(initialFilters[key], filters[key]) &&
      !ignoreFieldsList.includes(key)
    ) {
      count += 1;
    }
  });

  return count;
};

export const useFiltersCard = () => {
  const { handleCompletedFilterChange, completedFilters } =
    useAllWorklistContext();
  const [filters, setFilters] = useState<ICardFilters>(initialFilters);

  const metadata = useAllWorklistMetadataQuery({
    completed: true,
  });
  const buildingsQuery = useBuildingsShortQuery({});
  const { data: teamData = [] } = useTeamsShortQuery({});
  const { data: practiceData = [] } = usePracticeShortQuery({});
  const { data: userData = [] } = useUserListShortQuery({});
  const { data: taskFilterOptions } = useTaskFilterOptionsQuery({
    completed: true,
  });

  const teamOptions =
    teamData.map(team => ({
      value: team.id,
      label: team.name,
    })) || [];
  const practiceOptions =
    practiceData.map(practice => ({
      value: practice.id,
      label: practice.name,
    })) || [];

  const userOptions =
    userData.map(user => ({
      value: user.id,
      label: user.name,
    })) || [];

  const taskProgramOptions: Option<ICardFilters['programs'][number]>[] = [
    {
      value: 'GUIDE',
      label: `GUIDE (${metadata.data?.task_programs?.GUIDE})`,
    },
    {
      value: 'Pharmacy',
      label: `Pharmacy (${metadata.data?.task_programs?.Pharmacy})`,
    },
    {
      value: 'Optum MA',
      label: `Optum MA (${metadata.data?.task_programs?.['Optum MA']})`,
    },
    {
      value: 'CCM',
      label: `CCM (${metadata.data?.task_programs?.['CCM']})`,
    },
    {
      value: 'other',
      label: `Other (${metadata.data?.task_programs?.other})`,
    },
  ];

  const completedTimeOptions: Option<
    Exclude<ICardFilters['completion_interval'] | string, undefined>
  >[] = [
    { value: '', label: 'All time' },
    { value: 'month', label: 'Last Month' },
    { value: 'week', label: 'Last week' },
    { value: 'day', label: 'This week' },
  ];

  const categoryOptions = metadata.data?.categories || [];

  const locationOptions =
    buildingsQuery.data?.map(building => ({
      value: building.id,
      label: building.name,
    })) || [];

  const handleTeamOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      team_id: event.target.value,
    }));
  };

  const handlePracticeOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      practice_id: event.target.value,
    }));
  };

  const handleAssigneeOnChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      assignee_id: event.target.value,
    }));
  };

  const handleCategoryOnChange = (e: SelectChangeEvent<string>) => {
    setFilters(prev => ({
      ...prev,
      category: e.target.value as string,
    }));
  };

  const handleLocationOnChange = (values: string[]) => {
    setFilters(prev => ({
      ...prev,
      location_building_ids: values,
    }));
  };

  const handleClearAll = () => {
    handleCompletedFilterChange({
      ...initialFilters,
      provider_ids: completedFilters.provider_ids,
      partner_id: completedFilters.partner_id,
      search: completedFilters.search,
      statuses: ['Completed'],
      completion_interval: initialFilters.completion_interval,
    });
    setFilters(initialFilters);
  };

  const handleApply = () => {
    handleCompletedFilterChange({
      ...initialFilters,
      completion_interval: filters.completion_interval,
      programs: filters.programs,
      statuses: ['Completed'],
      category: filters.category,
      team_id: filters.team_id,
      practice_id: filters.practice_id,
      assignee_id: filters.assignee_id,
      location_building_ids: filters.location_building_ids,
      task_names: filters.task_names,
      order_categories: filters.order_categories,
      order_types: filters.order_types,
      provider_ids: completedFilters.provider_ids,
      partner_id: completedFilters.partner_id,
      search: completedFilters.search,
      appliedFilters: getAppliedFiltersCount({
        ...filters,
        provider_ids: [],
      }),
    });
  };

  const handleCompletionIntervalOnChange = (val: string) => {
    setFilters(prev => ({
      ...prev,
      completion_interval: val as CompletionIntervalType,
    }));
  };

  const handleTaskNameOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['task_names'];
    setFilters(prev => ({
      ...prev,
      task_names: value,
    }));
  };

  const handleProgramOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['programs'];
    setFilters(prev => ({
      ...prev,
      programs: value,
    }));
  };

  const handleOrderCategoryOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_categories'];
    setFilters(prev => ({
      ...prev,
      order_categories: value,
    }));
  };

  const handleOrderTypeOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_types'];
    setFilters(prev => ({
      ...prev,
      order_types: value,
    }));
  };

  useEffect(() => {
    setFilters({ ...completedFilters });
  }, [completedFilters]);

  return {
    handleClearAll,
    handleApply,
    filters,
    taskProgramOptions,
    categoryOptions: categoryOptions.map(o => ({
      value: o,
      label: o,
    })),
    locationOptions,
    userOptions,
    teamOptions,
    practiceOptions,
    handleTeamOnChange,
    handlePracticeOnChange,
    handleAssigneeOnChange,
    handleProgramOnChange,
    handleCategoryOnChange,
    handleLocationOnChange,
    completedTimeOptions,
    handleCompletionIntervalOnChange,
    resetIsDisabled: isEqual(filters, initialFilters),
    handleTaskNameOnChange,
    handleOrderCategoryOnChange,
    handleOrderTypeOnChange,
    taskNameOptions: taskFilterOptions?.task_names.map(o => ({
      value: o,
      label: o,
    })),
    orderTypeOptions: taskFilterOptions?.order_types.map(o => ({
      value: o,
      label: o,
    })),
    orderCategoryOptions: taskFilterOptions?.order_categories.map(o => ({
      value: o,
      label: o,
    })),
  };
};
